import React, { useState, useMemo, useCallback } from 'react';
import {
  Scissors,
  Loader2,
  Wand2,
  Shuffle,
  Filter,
  Trash2,
  RefreshCw,
  Edit3,
  X
} from 'lucide-react';
import {
  autoFixKeywords,
  shuffleKeywords,
  removePlurals,
  splitToSingleWords,
  removeMultiWordKeywords,
  findAndReplaceKeywords,
  normalizeKeyword,
  detectDuplicates
} from '../../utils/autoFix';

interface CSVRow {
  filename: string;
  title: string;
  description: string;
  keywords: string;
}

interface SimpleBatchActionsProps {
  csvData: { headers: string[]; rows: CSVRow[] };
  onUpdateAll: (updatedRows: CSVRow[]) => void;
}


export const SimpleBatchActions: React.FC<SimpleBatchActionsProps> = ({
  csvData,
  onUpdateAll
}) => {
  const LARGE_DATASET_THRESHOLD = 500;
  const isLargeDataset = csvData.rows.length > LARGE_DATASET_THRESHOLD;
  const [showTrimModal, setShowTrimModal] = useState(false);
  const [trimValue, setTrimValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAutoFixing, setIsAutoFixing] = useState(false);
  const [showAutoFixConfirm, setShowAutoFixConfirm] = useState(false);
  const [showTrimConfirm, setShowTrimConfirm] = useState(false);
  const [showShuffleConfirm, setShowShuffleConfirm] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);
  const [showPluralsConfirm, setShowPluralsConfirm] = useState(false);
  const [isRemovingPlurals, setIsRemovingPlurals] = useState(false);
  const [showSplitConfirm, setShowSplitConfirm] = useState(false);
  const [isSplitting, setIsSplitting] = useState(false);
  const [showMultiWordConfirm, setShowMultiWordConfirm] = useState(false);
  const [isRemovingMultiWord, setIsRemovingMultiWord] = useState(false);
  const [showFindReplaceModal, setShowFindReplaceModal] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [matchWholeKeyword, setMatchWholeKeyword] = useState(false);
  const [showFindReplaceConfirm, setShowFindReplaceConfirm] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [showFindDeleteModal, setShowFindDeleteModal] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [showDeleteSuggestions, setShowDeleteSuggestions] = useState(false);
  const [deleteFilteredSuggestions, setDeleteFilteredSuggestions] = useState<string[]>([]);
  const [showFindDeleteConfirm, setShowFindDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditContentModal, setShowEditContentModal] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editKeywords, setEditKeywords] = useState('');
  const [editFilename, setEditFilename] = useState('');
  const [editMode, setEditMode] = useState<'append' | 'prepend'>('prepend');
  const [isEditingContent, setIsEditingContent] = useState(false);

  // Calculate if auto-fix is needed
  const needsAutoFix = useMemo(() => {
    return csvData.rows.some(row => {
      const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
      return detectDuplicates(keywords).length > 0;
    });
  }, [csvData.rows]);

  // Get shuffleable files count
  const getShuffleableFilesCount = () => {
    return csvData.rows.filter(row => {
      const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
      return keywords.length > 1;
    }).length;
  };

  // Batch Trim function
  const handleBatchTrim = async () => {
    const trimCount = parseInt(trimValue);
    if (!trimCount || trimCount <= 0) return;

    setIsProcessing(true);
    setShowTrimConfirm(false);

    try {
      const updatedRows = csvData.rows.map(row => {
        const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
        const trimmedKeywords = keywords.slice(0, trimCount);

        return {
          ...row,
          keywords: trimmedKeywords.join(', ')
        };
      });

      onUpdateAll(updatedRows);
      setShowTrimModal(false);
      setTrimValue('');
    } finally {
      setIsProcessing(false);
    }
  };

  // Batch Quick Fix function
  const handleBatchAutoFix = async () => {
    setIsAutoFixing(true);
    setShowAutoFixConfirm(false);

    try {
      const updatedRows = csvData.rows.map(row => {
        const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
        const fixedKeywords = autoFixKeywords(keywords);

        return {
          ...row,
          keywords: fixedKeywords.fixedKeywords.join(', ')
        };
      });

      onUpdateAll(updatedRows);
    } finally {
      setIsAutoFixing(false);
    }
  };

  // Batch Shuffle function
  const handleBatchShuffle = async () => {
    setIsShuffling(true);
    setShowShuffleConfirm(false);

    try {
      const updatedRows = csvData.rows.map(row => {
        const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
        const shuffledKeywords = shuffleKeywords(keywords);

        return {
          ...row,
          keywords: shuffledKeywords.join(', ')
        };
      });

      onUpdateAll(updatedRows);
    } finally {
      setIsShuffling(false);
    }
  };

  // Batch Remove Plurals function
  const handleBatchRemovePlurals = async () => {
    setIsRemovingPlurals(true);
    setShowPluralsConfirm(false);

    try {
      const updatedRows = csvData.rows.map(row => {
        const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
        const fixedKeywords = removePlurals(keywords);

        return {
          ...row,
          keywords: fixedKeywords.join(', ')
        };
      });

      onUpdateAll(updatedRows);
    } finally {
      setIsRemovingPlurals(false);
    }
  };

  // Batch Split into Single Words function
  const handleBatchSplitToSingleWords = async () => {
    setIsSplitting(true);
    setShowSplitConfirm(false);

    try {
      const updatedRows = csvData.rows.map(row => {
        const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
        const splitKeywords = splitToSingleWords(keywords);

        return {
          ...row,
          keywords: splitKeywords.join(', ')
        };
      });

      onUpdateAll(updatedRows);
    } finally {
      setIsSplitting(false);
    }
  };

  // Batch Remove Multi-Word function
  const handleBatchRemoveMultiWord = async () => {
    setIsRemovingMultiWord(true);
    setShowMultiWordConfirm(false);

    try {
      const updatedRows = csvData.rows.map(row => {
        const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
        const singleWordKeywords = removeMultiWordKeywords(keywords);

        return {
          ...row,
          keywords: singleWordKeywords.join(', ')
        };
      });

      onUpdateAll(updatedRows);
    } finally {
      setIsRemovingMultiWord(false);
    }
  };

  // Get preview of Find & Delete changes
  const getFindDeletePreview = useCallback(() => {
    if (!selectedKeywords.length) return { affectedFiles: 0, totalRemovals: 0 };

    let affectedFiles = 0;
    let totalRemovals = 0;

    csvData.rows.forEach(row => {
      const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
      const originalLength = keywords.length;
      const filteredKeywords = keywords.filter(keyword =>
        !selectedKeywords.some(selected =>
          matchWholeKeyword
            ? normalizeKeyword(keyword) === normalizeKeyword(selected)
            : keyword.toLowerCase().includes(selected.toLowerCase())
        )
      );

      if (filteredKeywords.length < originalLength) {
        affectedFiles++;
        totalRemovals += (originalLength - filteredKeywords.length);
      }
    });

    return { affectedFiles, totalRemovals };
  }, [selectedKeywords, matchWholeKeyword, csvData.rows]);

  // Batch Find & Delete function
  const handleBatchFindDelete = async () => {
    if (!selectedKeywords.length) return;

    setIsDeleting(true);
    setShowFindDeleteConfirm(false);

    try {
      const updatedRows = csvData.rows.map(row => {
        const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
        const filteredKeywords = keywords.filter(keyword =>
          !selectedKeywords.some(selected =>
            matchWholeKeyword
              ? normalizeKeyword(keyword) === normalizeKeyword(selected)
              : keyword.toLowerCase().includes(selected.toLowerCase())
          )
        );

        return {
          ...row,
          keywords: filteredKeywords.join(', ')
        };
      });

      onUpdateAll(updatedRows);
      setShowFindDeleteModal(false);
      setSelectedKeywords([]);
      setCurrentInput('');
    } finally {
      setIsDeleting(false);
    }
  };

  // Get preview of Find & Replace changes
  const getFindReplacePreview = () => {
    if (!findText.trim()) return { affectedFiles: 0, totalReplacements: 0 };

    let affectedFiles = 0;
    let totalReplacements = 0;

    csvData.rows.forEach(row => {
      const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];

      const originalCount = keywords.filter(k =>
        matchWholeKeyword
          ? normalizeKeyword(k) === normalizeKeyword(findText)
          : k.toLowerCase().includes(findText.toLowerCase())
      ).length;

      if (originalCount > 0) {
        affectedFiles++;
        totalReplacements += originalCount;
      }
    });

    return { affectedFiles, totalReplacements };
  };

  // Batch Find & Replace function
  const handleBatchFindReplace = async () => {
    if (!findText.trim()) return;

    setIsReplacing(true);
    setShowFindReplaceConfirm(false);

    try {
      const updatedRows = csvData.rows.map(row => {
        const keywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
        const replacedKeywords = findAndReplaceKeywords(keywords, findText, replaceText, matchWholeKeyword);

        return {
          ...row,
          keywords: replacedKeywords.join(', ')
        };
      });

      onUpdateAll(updatedRows);
      setShowFindReplaceModal(false);
      setFindText('');
      setReplaceText('');
    } finally {
      setIsReplacing(false);
    }
  };

  // Handle Edit Content function
  const handleEditContent = async () => {
    // Validation: at least one field must be filled
    if (!editFilename.trim() && !editTitle.trim() && !editDescription.trim() && !editKeywords.trim()) {
      alert('Please fill at least one field (filename, title, description, or keywords)');
      return;
    }

    setIsEditingContent(true);

    try {
      const updatedRows = csvData.rows.map(row => {
        let newRow = { ...row };

        // Handle filename
        if (editFilename.trim()) {
          const currentFilename = row.filename || '';
          if (editMode === 'prepend') {
            newRow.filename = `${editFilename.trim()}${currentFilename}`.trim();
          } else {
            newRow.filename = `${currentFilename}${editFilename.trim()}`.trim();
          }
        }

        // Handle title
        if (editTitle.trim()) {
          const currentTitle = row.title || '';
          if (editMode === 'prepend') {
            newRow.title = `${editTitle.trim()} ${currentTitle}`.trim();
          } else {
            newRow.title = `${currentTitle} ${editTitle.trim()}`.trim();
          }
        }

        // Handle description
        if (editDescription.trim()) {
          const currentDescription = row.description || '';
          if (editMode === 'prepend') {
            newRow.description = `${editDescription.trim()} ${currentDescription}`.trim();
          } else {
            newRow.description = `${currentDescription} ${editDescription.trim()}`.trim();
          }
        }

        // Handle keywords
        if (editKeywords.trim()) {
          const currentKeywords = row.keywords ? row.keywords.split(',').map(k => k.trim()) : [];
          const newKeywords = editKeywords.split(',').map(k => k.trim()).filter(k => k.length > 0);

          if (editMode === 'prepend') {
            const combinedKeywords = [...newKeywords, ...currentKeywords];
            newRow.keywords = combinedKeywords.join(', ');
          } else {
            const combinedKeywords = [...currentKeywords, ...newKeywords];
            newRow.keywords = combinedKeywords.join(', ');
          }
        }

        return newRow;
      });

      onUpdateAll(updatedRows);
      setShowEditContentModal(false);
      setEditTitle('');
      setEditDescription('');
      setEditKeywords('');
    } finally {
      setIsEditingContent(false);
    }
  };

  if (!csvData || csvData.rows.length === 0) {
    return null;
  }

  const trimPreview = trimValue ? parseInt(trimValue) : 0;
  const findDeletePreview = getFindDeletePreview();
  const findReplacePreview = getFindReplacePreview();

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        {/* Large Dataset Warning */}
        {isLargeDataset && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  Large Dataset ({csvData.rows.length} rows)
                </h3>
                <div className="mt-1 text-sm text-amber-700">
                  <p>Batch operations may take longer to process. For better performance, consider splitting large files into smaller chunks.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {(isProcessing || isAutoFixing || isShuffling || isRemovingPlurals || isSplitting || isRemovingMultiWord || isReplacing || isDeleting || isEditingContent) && (
          <div className="flex items-center justify-center text-sm text-blue-600 mb-4">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing...
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-9 gap-2 w-full">
              {/* Trim - Active Feature */}
              <button
                onClick={() => setShowTrimModal(true)}
                disabled={isProcessing}
                className="flex flex-col items-center p-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Trim keywords to specified count"
              >
                <Scissors className="w-3 h-3 text-gray-600 mb-1" />
                <span className="text-xs text-gray-700 font-medium">Trim</span>
              </button>

              {/* Quick Fix - Active Feature */}
              <button
                onClick={() => setShowAutoFixConfirm(true)}
                disabled={isProcessing || isAutoFixing || !needsAutoFix}
                className={`flex flex-col items-center p-2 border rounded-lg transition-colors ${
                  needsAutoFix
                    ? 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    : 'bg-gray-100 border-gray-300 opacity-50 cursor-not-allowed'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title={needsAutoFix ? "Quick fix duplicate keywords across all files" : "No duplicate keywords found to fix"}
              >
                <Wand2 className={`w-3 h-3 mb-1 ${needsAutoFix ? 'text-gray-600' : 'text-gray-400'}`} />
                <span className={`text-xs font-medium ${needsAutoFix ? 'text-gray-700' : 'text-gray-500'}`}>Quick Fix</span>
              </button>

              {/* Shuffle - Active Feature */}
              <button
                onClick={() => setShowShuffleConfirm(true)}
                disabled={isProcessing || isShuffling || getShuffleableFilesCount() === 0}
                className="flex flex-col items-center p-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Randomize keyword order across all files"
              >
                <Shuffle className="w-3 h-3 text-gray-600 mb-1" />
                <span className="text-xs text-gray-700 font-medium">Shuffle</span>
              </button>

              {/* Remove Plurals - Active Feature */}
              <button
                onClick={() => setShowPluralsConfirm(true)}
                disabled={isProcessing || isRemovingPlurals}
                className="flex flex-col items-center p-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Remove plural keywords across all files"
              >
                <Filter className="w-3 h-3 text-gray-600 mb-1" />
                <span className="text-xs text-gray-700 font-medium">No Plurals</span>
              </button>

              {/* Split to Single Words - Active Feature */}
              <button
                onClick={() => setShowSplitConfirm(true)}
                disabled={isProcessing || isSplitting}
                className="flex flex-col items-center p-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Split compound keywords into single words"
              >
                <Scissors className="w-3 h-3 text-gray-600 mb-1" />
                <span className="text-xs text-gray-700 font-medium">Split</span>
              </button>

              {/* Remove Multi-Word - Active Feature */}
              <button
                onClick={() => setShowMultiWordConfirm(true)}
                disabled={isProcessing || isRemovingMultiWord}
                className="flex flex-col items-center p-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Remove multi-word keywords, keep only single words"
              >
                <Trash2 className="w-3 h-3 text-gray-600 mb-1" />
                <span className="text-xs text-gray-700 font-medium">Single Only</span>
              </button>

              {/* Find & Replace - Active Feature */}
              <button
                onClick={() => setShowFindReplaceModal(true)}
                disabled={isProcessing || isReplacing}
                className="flex flex-col items-center p-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Find and replace keywords across all files"
              >
                <RefreshCw className="w-3 h-3 text-gray-600 mb-1" />
                <span className="text-xs text-gray-700 font-medium">Find & Replace</span>
              </button>

              {/* Find & Delete - Active Feature */}
              <button
                onClick={() => setShowFindDeleteModal(true)}
                disabled={isProcessing || isDeleting}
                className="flex flex-col items-center p-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Find and delete keywords across all files"
              >
                <Trash2 className="w-3 h-3 text-gray-600 mb-1" />
                <span className="text-xs text-gray-700 font-medium">Find & Delete</span>
              </button>

              {/* Edit Content - Active Feature */}
              <button
                onClick={() => setShowEditContentModal(true)}
                disabled={isProcessing || isEditingContent}
                className="flex flex-col items-center p-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Replace all keywords with custom content"
              >
                <Edit3 className="w-3 h-3 text-gray-600 mb-1" />
                <span className="text-xs text-gray-700 font-medium">Edit Content</span>
              </button>
        </div>
      </div>

      {/* Trim Modal */}
      {showTrimModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Trim Keywords</h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter the maximum number of keywords to keep per file. Excess keywords will be removed from the end.
            </p>
            <div className="mb-4">
              <input
                type="number"
                value={trimValue}
                onChange={(e) => setTrimValue(e.target.value)}
                placeholder="30"
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-200 focus:outline-none"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowTrimConfirm(true)}
                disabled={!trimValue.trim() || parseInt(trimValue) <= 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Trim Keywords
              </button>
              <button
                onClick={() => {
                  setShowTrimModal(false);
                  setTrimValue('');
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trim Confirmation */}
      {showTrimConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirm Trim</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will trim keywords to <strong>{trimPreview}</strong> per file across <strong>{csvData.rows.length}</strong> files.
              Any keywords beyond this limit will be removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleBatchTrim}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirm Trim
              </button>
              <button
                onClick={() => setShowTrimConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto Fix Confirmation */}
      {showAutoFixConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Fix Keywords</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will automatically fix duplicate keywords and optimize keyword lists across all files.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleBatchAutoFix}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Quick Fix
              </button>
              <button
                onClick={() => setShowAutoFixConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shuffle Confirmation */}
      {showShuffleConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Shuffle Keywords</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will randomize the order of keywords in <strong>{getShuffleableFilesCount()}</strong> files that have multiple keywords.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleBatchShuffle}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Shuffle Keywords
              </button>
              <button
                onClick={() => setShowShuffleConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Plurals Confirmation */}
      {showPluralsConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Remove Plural Keywords</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will remove plural endings from keywords across all <strong>{csvData.rows.length}</strong> files.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleBatchRemovePlurals}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Remove Plurals
              </button>
              <button
                onClick={() => setShowPluralsConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split Confirmation */}
      {showSplitConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Split Compound Keywords</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will split compound keywords (containing spaces or hyphens) into individual words across all files.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleBatchSplitToSingleWords}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Split Keywords
              </button>
              <button
                onClick={() => setShowSplitConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Multi-Word Confirmation */}
      {showMultiWordConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Remove Multi-Word Keywords</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will remove all keywords containing spaces or hyphens, keeping only single words.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleBatchRemoveMultiWord}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Remove Multi-Word
              </button>
              <button
                onClick={() => setShowMultiWordConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Find & Replace Modal */}
      {showFindReplaceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Find & Replace Keywords</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Find</label>
                <input
                  type="text"
                  value={findText}
                  onChange={(e) => setFindText(e.target.value)}
                  placeholder="Keyword to find..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-200 focus:outline-none"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Replace with</label>
                <input
                  type="text"
                  value={replaceText}
                  onChange={(e) => setReplaceText(e.target.value)}
                  placeholder="Replacement text..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-200 focus:outline-none"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="matchWhole"
                  checked={matchWholeKeyword}
                  onChange={(e) => setMatchWholeKeyword(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="matchWhole" className="ml-2 text-sm text-gray-700">
                  Match whole keyword only
                </label>
              </div>
              {findText && (
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  Will affect <strong>{findReplacePreview.affectedFiles}</strong> files with <strong>{findReplacePreview.totalReplacements}</strong> replacements
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowFindReplaceConfirm(true)}
                disabled={!findText.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Replace Keywords
              </button>
              <button
                onClick={() => {
                  setShowFindReplaceModal(false);
                  setFindText('');
                  setReplaceText('');
                  setMatchWholeKeyword(false);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Find & Replace Confirmation */}
      {showFindReplaceConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirm Find & Replace</h3>
            <p className="text-sm text-gray-600 mb-4">
              Replace "<strong>{findText}</strong>" with "<strong>{replaceText}</strong>" across <strong>{findReplacePreview.affectedFiles}</strong> files
              ({findReplacePreview.totalReplacements} total replacements).
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleBatchFindReplace}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirm Replace
              </button>
              <button
                onClick={() => setShowFindReplaceConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Find & Delete Modal */}
      {showFindDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Find & Delete Keywords</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Keywords to delete</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentInput}
                    onChange={(e) => {
                      setCurrentInput(e.target.value);
                      const filtered = csvData.rows
                        .flatMap(row => row.keywords ? row.keywords.split(',').map(k => k.trim()) : [])
                        .filter(k => k.toLowerCase().includes(e.target.value.toLowerCase()) && e.target.value.trim())
                        .filter((k, i, arr) => arr.indexOf(k) === i)
                        .slice(0, 10);
                      setDeleteFilteredSuggestions(filtered);
                      setShowDeleteSuggestions(e.target.value.trim().length > 0);
                    }}
                    placeholder="Type keyword to find..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-200 focus:outline-none"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && currentInput.trim()) {
                        if (!selectedKeywords.includes(currentInput.trim())) {
                          setSelectedKeywords([...selectedKeywords, currentInput.trim()]);
                        }
                        setCurrentInput('');
                        setShowDeleteSuggestions(false);
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (currentInput.trim() && !selectedKeywords.includes(currentInput.trim())) {
                        setSelectedKeywords([...selectedKeywords, currentInput.trim()]);
                        setCurrentInput('');
                        setShowDeleteSuggestions(false);
                      }
                    }}
                    disabled={!currentInput.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>

                {showDeleteSuggestions && deleteFilteredSuggestions.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                    {deleteFilteredSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedKeywords([...selectedKeywords, suggestion]);
                          setCurrentInput('');
                          setShowDeleteSuggestions(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedKeywords.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Selected keywords to delete</label>
                  <div className="flex flex-wrap gap-2">
                    {selectedKeywords.map((keyword, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                        {keyword}
                        <button
                          onClick={() => setSelectedKeywords(selectedKeywords.filter((_, i) => i !== index))}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="matchWholeDelete"
                  checked={matchWholeKeyword}
                  onChange={(e) => setMatchWholeKeyword(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="matchWholeDelete" className="ml-2 text-sm text-gray-700">
                  Match whole keyword only
                </label>
              </div>

              {selectedKeywords.length > 0 && (
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  Will affect <strong>{findDeletePreview.affectedFiles}</strong> files, removing <strong>{findDeletePreview.totalRemovals}</strong> keywords
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowFindDeleteConfirm(true)}
                disabled={selectedKeywords.length === 0}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Delete Keywords
              </button>
              <button
                onClick={() => {
                  setShowFindDeleteModal(false);
                  setSelectedKeywords([]);
                  setCurrentInput('');
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Find & Delete Confirmation */}
      {showFindDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirm Delete</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will remove the selected keywords from <strong>{findDeletePreview.affectedFiles}</strong> files
              ({findDeletePreview.totalRemovals} total removals).
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleBatchFindDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Confirm Delete
              </button>
              <button
                onClick={() => setShowFindDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Content Modal */}
      {showEditContentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Batch Edit Content</h3>
            <p className="text-sm text-gray-600 mb-6">
              Add content to filename, title, description, and/or keywords for all <strong>{csvData.rows.length}</strong> files.
            </p>

            {/* Filename Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filename
                <span className="text-xs text-gray-500 ml-1">
                  ({editFilename.length})
                </span>
              </label>
              <input
                type="text"
                value={editFilename}
                onChange={(e) => setEditFilename(e.target.value.slice(0, 50))}
                placeholder="e.g., photo"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={50}
              />
              <p className="text-xs text-gray-500 mt-1">
                Add your own separator (e.g., "_", "-", etc.). Spaces will be preserved.
              </p>
            </div>

            {/* Title Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
                <span className="text-xs text-gray-500 ml-1">
                  ({editTitle.length})
                </span>
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value.slice(0, 100))}
                placeholder="e.g., A beautiful nature photo of"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={100}
              />
            </div>

            {/* Description Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
                <span className="text-xs text-gray-500 ml-1">
                  ({editDescription.length})
                </span>
              </label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value.slice(0, 100))}
                placeholder="e.g., This stunning nature photo shows"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                maxLength={100}
              />
            </div>

            {/* Keywords Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords
                <span className="text-xs text-gray-500 ml-1">
                  ({editKeywords.split(',').filter(k => k.trim()).length} keywords)
                </span>
              </label>
              <input
                type="text"
                value={editKeywords}
                onChange={(e) => setEditKeywords(e.target.value)}
                placeholder="e.g., nature,landscape,outdoor"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separate keywords with commas (no spaces)
              </p>
            </div>

            {/* Append/Prepend Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mode
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="prepend"
                    checked={editMode === 'prepend'}
                    onChange={(e) => setEditMode(e.target.value as 'prepend' | 'append')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Prepend (add at beginning)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="append"
                    checked={editMode === 'append'}
                    onChange={(e) => setEditMode(e.target.value as 'prepend' | 'append')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Append (add at end)</span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowEditContentModal(false);
                  setEditFilename('');
                  setEditTitle('');
                  setEditDescription('');
                  setEditKeywords('');
                }}
                disabled={isEditingContent}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditContent}
                disabled={isEditingContent || (!editFilename.trim() && !editTitle.trim() && !editDescription.trim() && !editKeywords.trim())}
                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isEditingContent ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  'Update Content'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SimpleBatchActions;